import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import type { Nullable, UserWithToken, WithData } from './api.types';

/** Los estados de `MatchReplay.videoStatus`, para comparar sin string literals. */
export const ReplayVideoStatuses = {
  None: 'NONE',
  Pending: 'PENDING',
  Claimed: 'CLAIMED',
  Done: 'DONE',
  NotFound: 'NOT_FOUND',
  Failed: 'FAILED',
} as const;

export type ReplayVideoStatus =
  (typeof ReplayVideoStatuses)[keyof typeof ReplayVideoStatuses];

/** Estado del video del replay de una mesa (Mac mini, extensión replay). */
export type ReplayVideo = {
  status: ReplayVideoStatus;
  seconds: Nullable<number>;
  error: Nullable<string>;
};

/**
 * URL firmada (1 h) del video de la mesa; null si no está grabado. Con
 * `download`, la URL baja el archivo (`ronda-R-mesa-M.mp4`, o `.webm` los grabados antes de la 6.10.12) en vez de
 * reproducirlo. Staff del torneo.
 */
export const getReplayVideoUrl = async (
  user: Nullable<UserWithToken>,
  matchId: number,
  download = false
) =>
  getGraphQLQueryClient(user).request<WithData<Nullable<string>>>(
    gql`
      query replayVideoUrl($matchId: Int!, $download: Boolean) {
        data: replayVideoUrl(matchId: $matchId, download: $download)
      }
    `,
    { matchId, download }
  );

/** Vuelve a encolar el video de la mesa (desde FAILED, NOT_FOUND o NONE). */
export const retryReplayVideo = async (
  user: Nullable<UserWithToken>,
  matchId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation retryReplayVideo($matchId: Int!) {
        data: retryReplayVideo(matchId: $matchId)
      }
    `,
    { matchId }
  );

/** Encola el video de todas las mesas del torneo que ya tienen replay. */
export const enqueueTournamentReplayVideos = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<number>>(
    gql`
      mutation enqueueTournamentReplayVideos($tournamentId: Int!) {
        data: enqueueTournamentReplayVideos(tournamentId: $tournamentId)
      }
    `,
    { tournamentId }
  );
